import React from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import projects from "../data/projectsData";
import ProjectGallerySlider from "../components/ProjectGallerySlider";
import Seo from "../components/Seo";
import "../components/Section.css";
import "../components/Projects.css";
import { fadeUp, staggerContainer, staggerItem, viewportOnce, easing } from "../utils/motionVariants";

export default function ProjectDetailsPage(){
  const { slug } = useParams();
  const navigate = useNavigate();
  const p = projects.find(x => x.slug === slug);

  if(!p){
    return (
      <motion.section
        className="section"
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: easing }}
      >
        <Seo
          title="Project not found"
          path={`/project/${slug}`}
          description="The project you're looking for couldn't be found. Browse all projects in Hirun Kolongahapitiya's portfolio."
          noindex
        />
        <h2>Project not found</h2>
        <Link to="/portfolio" className="accent">← Back to Portfolio</Link>
      </motion.section>
    );
  }

  const seoTitle = p.seo?.title || p.title;
  const seoDescription = p.seo?.description || p.oneLiner;
  const projectUrl = `https://hirun.lk/project/${p.slug}`;
  const projectImage = p.cover || p.gallery?.[0]?.src;
  const projectJsonLd = {
    "@context": "https://schema.org",
    "@type": "CreativeWork",
    name: p.title,
    description: seoDescription,
    url: projectUrl,
    image: projectImage ? `https://hirun.lk${projectImage}` : undefined,
    keywords: [...(p.tech || []), ...(p.domain || [])].join(", "),
    creator: {
      "@type": "Person",
      name: "Hirun Kolongahapitiya",
      url: "https://hirun.lk",
    },
    ...(p.clientOrOrg && {
      publisher: { "@type": "Organization", name: p.clientOrOrg },
    }),
    ...(p.timeframe?.start && { dateCreated: p.timeframe.start }),
    ...(p.timeframe?.end && { dateModified: p.timeframe.end }),
  };

  const gallery = Array.isArray(p.gallery) ? p.gallery : [];
  const sliderImages = gallery.length
    ? gallery
    : p.cover
      ? [{ src: p.cover, alt: p.title }]
      : [];

  return (
    <section className="section section-white">
      <Seo
        title={seoTitle}
        path={`/project/${p.slug}`}
        description={seoDescription}
        image={projectImage}
        type="article"
        jsonLd={projectJsonLd}
      />
      <motion.div
        className="section-header"
        initial="hidden"
        animate="visible"
        variants={fadeUp}
      >
        <h2>{p.title}</h2>
        <div className="section-underline" />
      </motion.div>

      {sliderImages.length > 0 && (
        <motion.div
          initial={{ opacity: 0, scale: 0.97 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.1, duration: 0.5, ease: easing }}
        >
          <ProjectGallerySlider images={sliderImages} />
        </motion.div>
      )}

      <motion.div
        className="card"
        style={{ marginTop: 14 }}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
        variants={fadeUp}
        whileHover={{ y: -3, boxShadow: "0 18px 40px rgba(15, 23, 42, 0.1)" }}
        transition={{ duration: 0.25, ease: easing }}
      >
        <p className="muted" style={{ margin: 0 }}>{p.oneLiner}</p>
        <p className="muted" style={{ marginTop: 6 }}>
          {p.timeframe.start}{p.timeframe.end ? ` - ${p.timeframe.end}` : ""} • {p.timeframe.status}
        </p>
        <p><strong>Domain:</strong> {p.domain.join(", ")}</p>
        <p><strong>Tech:</strong> {p.tech.join(", ")}</p>
        {p.links?.live && (
          <p>
            <motion.a
              href={p.links.live}
              target="_blank"
              rel="noreferrer"
              className="accent"
              whileHover={{ x: 4 }}
              transition={{ duration: 0.2 }}
            >
              🔗 View Live
            </motion.a>
          </p>
        )}
        {p.links?.demo && (
          <p>
            <motion.a
              href={p.links.demo}
              target="_blank"
              rel="noreferrer"
              className="accent"
              whileHover={{ x: 4 }}
              transition={{ duration: 0.2 }}
            >
              🔗 DEMO
            </motion.a>
          </p>
        )}
        {p.links?.apk && (
          <p>
            <motion.a
              href={p.links.apk}
              download
              className="accent"
              whileHover={{ x: 4 }}
              transition={{ duration: 0.2 }}
            >
              ⬇️ Download Android APK
            </motion.a>
          </p>
        )}
      </motion.div>

      <motion.div
        className="grid-2"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        <motion.div
          className="card"
          variants={staggerItem}
          whileHover={{ y: -4, boxShadow: "0 18px 40px rgba(15, 23, 42, 0.1)" }}
          transition={{ duration: 0.25, ease: easing }}
        >
          <h3 style={{ marginTop: 0 }}>Impact</h3>
          <ul>{p.impact.map((i, idx) => <li key={idx}>{i}</li>)}</ul>
        </motion.div>
        <motion.div
          className="card"
          variants={staggerItem}
          whileHover={{ y: -4, boxShadow: "0 18px 40px rgba(15, 23, 42, 0.1)" }}
          transition={{ duration: 0.25, ease: easing }}
        >
          <h3 style={{ marginTop: 0 }}>Responsibilities</h3>
          <ul>{p.responsibilities.map((i, idx) => <li key={idx}>{i}</li>)}</ul>
        </motion.div>
      </motion.div>

      <motion.div
        className="grid-2"
        variants={staggerContainer}
        initial="hidden"
        whileInView="visible"
        viewport={viewportOnce}
      >
        <motion.div
          className="card"
          variants={staggerItem}
          whileHover={{ y: -4, boxShadow: "0 18px 40px rgba(15, 23, 42, 0.1)" }}
          transition={{ duration: 0.25, ease: easing }}
        >
          <h3 style={{ marginTop: 0 }}>Key Features</h3>
          <ul>{p.features.map((i, idx) => <li key={idx}>{i}</li>)}</ul>
        </motion.div>
        <motion.div
          className="card"
          variants={staggerItem}
          whileHover={{ y: -4, boxShadow: "0 18px 40px rgba(15, 23, 42, 0.1)" }}
          transition={{ duration: 0.25, ease: easing }}
        >
          <h3 style={{ marginTop: 0 }}>Architecture</h3>
          <ul>{p.architecture.map((i, idx) => <li key={idx}>{i}</li>)}</ul>
        </motion.div>
      </motion.div>

      <motion.div
        style={{ display: "flex", gap: 12, marginTop: 10 }}
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={viewportOnce}
        transition={{ duration: 0.3 }}
      >
        <motion.button
          onClick={() => navigate(-1)}
          className="btn-back"
          whileHover={{ x: -4, scale: 1.03 }}
          whileTap={{ scale: 0.96 }}
        >
          ← Back
        </motion.button>
        <motion.div whileHover={{ scale: 1.03 }} whileTap={{ scale: 0.96 }} style={{ display: "inline-block" }}>
          <Link to="/portfolio" className="btn-back">Back to Portfolio</Link>
        </motion.div>
      </motion.div>
    </section>
  );
}
